/**
 * Stretch Simulator — Dev page for testing the stretch exploration engine.
 * Fully client-side: generates a map, configures players + crawl strategy,
 * then steps through stretch turns interactively.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Play, RotateCcw, Plus, Minus, ArrowLeft,
  Flame, Users, Footprints, ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

// Grid map
import { GridMapCanvas } from '@/components/game-ui/GridMapCanvas';
import {
  generateMap, addEntity, getCell,
  DEFAULT_RENDER_CONFIG, DEFAULT_GENERATOR_CONFIG,
  type GridMap, type GridMapState, type GeneratorConfig,
  type GeneratorType, type RenderConfig,
} from '@omen-quest/grid-map';

// Stretch engine
import {
  createStretchState, stepStretch, createLightSource, toggleStealthMode,
  type StretchState, type StretchActivity, type StretchConfig,
  type LightSourceType, type WanderingFrequency, type WanderingLikelihood,
  type EncounterTableEntry,
} from '@omen-quest/stretch-engine';

// Stretch UI components
import { StretchStatusBar } from '@/components/game-ui/stretch/StretchStatusBar';
import { ActivityPanel } from '@/components/game-ui/stretch/ActivityPanel';
import { CountdownDisplay } from '@/components/game-ui/stretch/CountdownDisplay';
import { CrawlStrategyPanel } from '@/components/game-ui/stretch/CrawlStrategyPanel';
import { StretchResolutionPanel } from '@/components/game-ui/stretch/StretchResolutionPanel';

// ============================================================================
// TYPES
// ============================================================================

type SimMode = 'setup' | 'playing';

interface PlayerSetup {
  id: string;
  name: string;
  lightType: LightSourceType;
}

interface MonsterSetup {
  frequency: WanderingFrequency;
  likelihood: WanderingLikelihood;
  addSampleTable: boolean;
}

interface ExtrasSetup {
  addCountdown: boolean;
  addTimelineEvent: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const GENERATOR_LABELS: Record<GeneratorType, string> = {
  bsp_dungeon: 'BSP Dungeon',
  cave: 'Cave',
  arena: 'Arena',
  city: 'City',
  preset: 'Preset',
};

const LIGHT_LABELS: Record<LightSourceType, string> = {
  torch: 'Torch (r8, 6 str)',
  lantern: 'Lantern (r10, 36 str)',
  magic: 'Magic (r12, permanent)',
  other: 'Other (r6, 12 str)',
};

const FREQUENCY_LABELS: Record<WanderingFrequency, string> = {
  constant: 'Constant (every stretch)',
  medium: 'Medium (every 3)',
  hourly: 'Hourly (every 6)',
};

const LIKELIHOOD_LABELS: Record<WanderingLikelihood, string> = {
  unlikely: 'Unlikely (1 on d6)',
  possible: 'Possible (1-2 on d6)',
  inevitable: 'Inevitable (1-3 on d6)',
};

const SAMPLE_ENCOUNTER_TABLE: EncounterTableEntry[] = [
  { roll: [1, 2], encounterId: 'rats', description: 'A swarm of giant rats', difficulty: 'trivial' },
  { roll: [3, 4], encounterId: 'skeletons', description: 'Shambling skeletons', difficulty: 'challenging' },
  { roll: [5, 6], encounterId: 'wraith', description: 'A spectral wraith', difficulty: 'deadly' },
];

const DEFAULT_PLAYERS: PlayerSetup[] = [
  { id: 'p1', name: 'Scout', lightType: 'torch' },
  { id: 'p2', name: 'Mage', lightType: 'magic' },
];

const STRETCH_RENDER_CONFIG: RenderConfig = {
  ...DEFAULT_RENDER_CONFIG,
  tileSize: 24,
  fogEnabled: true,
  showGrid: true,
  showMinimap: true,
  showTriggers: false,
  showDebugVis: false,
};

const MOVEMENT_ACTIVITIES = new Set(['move', 'explore', 'move_quietly']);

// ============================================================================
// COMPONENT
// ============================================================================

export function StretchSimulator() {
  const [mode, setMode] = useState<SimMode>('setup');

  // Setup state
  const [genConfig, setGenConfig] = useState<GeneratorConfig>({
    ...DEFAULT_GENERATOR_CONFIG,
    width: 40,
    height: 30,
    type: 'bsp_dungeon',
  });
  const [playerConfigs, setPlayerConfigs] = useState<PlayerSetup[]>([...DEFAULT_PLAYERS]);
  const [monsterConfig, setMonsterConfig] = useState<MonsterSetup>({
    frequency: 'medium',
    likelihood: 'possible',
    addSampleTable: true,
  });
  const [extras, setExtras] = useState<ExtrasSetup>({
    addCountdown: true,
    addTimelineEvent: true,
  });

  // Playing state
  const [stretchState, setStretchState] = useState<StretchState | null>(null);
  const [activePlayerId, setActivePlayerId] = useState('');
  const [pendingActivities, setPendingActivities] = useState<Record<string, StretchActivity>>({});
  const [logEntries, setLogEntries] = useState<string[]>([]);
  const [renderConfig, setRenderConfig] = useState<RenderConfig>({ ...STRETCH_RENDER_CONFIG });

  const logRef = useRef<HTMLDivElement>(null);

  // Auto-scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logEntries]);

  // ============================================================================
  // SETUP HELPERS
  // ============================================================================

  const addPlayer = useCallback(() => {
    if (playerConfigs.length >= 4) return;
    const num = playerConfigs.length + 1;
    setPlayerConfigs(prev => [...prev, {
      id: `p${num}`,
      name: `Player ${num}`,
      lightType: 'torch',
    }]);
  }, [playerConfigs.length]);

  const removePlayer = useCallback(() => {
    if (playerConfigs.length <= 1) return;
    setPlayerConfigs(prev => prev.slice(0, -1));
  }, [playerConfigs.length]);

  const updatePlayer = useCallback((idx: number, updates: Partial<PlayerSetup>) => {
    setPlayerConfigs(prev => prev.map((p, i) => i === idx ? { ...p, ...updates } : p));
  }, []);

  const randomizeSeed = useCallback(() => {
    setGenConfig(prev => ({ ...prev, seed: Math.floor(Math.random() * 999999) }));
  }, []);

  // ============================================================================
  // START EXPLORATION
  // ============================================================================

  const startExploration = useCallback(() => {
    // Generate the map
    const map = generateMap(genConfig);

    // Find floor tiles to place players
    const floorTiles: Array<{ x: number; y: number }> = [];
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const cell = getCell(map, x, y);
        if (cell.terrain === 'floor') {
          floorTiles.push({ x, y });
        }
      }
    }

    if (floorTiles.length < playerConfigs.length) {
      appendLog('Error: Not enough floor tiles to place players');
      return;
    }

    // Place players on scattered floor tiles
    const spacing = Math.max(1, Math.floor(floorTiles.length / (playerConfigs.length + 1)));
    const playerPositions = playerConfigs.map((_, i) => floorTiles[spacing * (i + 1)] ?? floorTiles[i]);

    // Add player entities to the map for rendering
    let mapWithEntities = map;
    playerConfigs.forEach((pc, i) => {
      mapWithEntities = addEntity(mapWithEntities, {
        id: pc.id,
        x: playerPositions[i].x,
        y: playerPositions[i].y,
        type: 'player',
        name: pc.name,
      });
    });

    // Build light sources
    const lightSources = playerConfigs.map(pc =>
      createLightSource(`light-${pc.id}`, pc.id, pc.lightType),
    );

    // Build config
    const config: StretchConfig = {
      map: mapWithEntities,
      players: playerConfigs.map((pc, i) => ({
        id: pc.id,
        name: pc.name,
        x: playerPositions[i].x,
        y: playerPositions[i].y,
      })),
      crawlStrategy: { lightSources, stealthMode: false },
      wanderingMonsters: {
        frequency: monsterConfig.frequency,
        likelihood: monsterConfig.likelihood,
        encounterTable: monsterConfig.addSampleTable ? SAMPLE_ENCOUNTER_TABLE : [],
      },
      timeline: extras.addTimelineEvent ? [{
        id: 'sample-event',
        triggerStretch: 3,
        type: 'environment',
        data: { description: 'The dungeon rumbles ominously. Dust falls from the ceiling.' },
      }] : [],
      countdowns: extras.addCountdown ? [{
        id: 'sample-countdown',
        label: 'Ritual completes',
        remainingStretches: 5,
        visible: true,
        effect: {
          id: 'ritual-complete',
          triggerStretch: 0,
          type: 'narrative',
          data: { description: 'The ritual is complete! A dark portal opens.' },
        },
      }] : [],
    };

    const state = createStretchState(config);
    setStretchState(state);
    setActivePlayerId(playerConfigs[0].id);
    setPendingActivities({});
    setLogEntries(['Exploration begins. Stretch 1 — Planning phase.']);
    setMode('playing');
  }, [genConfig, playerConfigs, monsterConfig, extras]);

  // ============================================================================
  // LOG HELPER
  // ============================================================================

  const appendLog = useCallback((msg: string) => {
    setLogEntries(prev => [...prev, msg]);
  }, []);

  // ============================================================================
  // COMMIT & RESOLVE
  // ============================================================================

  /** Resolve the stretch after all players have committed, then log results. */
  const resolveAndLog = useCallback((state: StretchState): StretchState => {
    if (state.phase !== 'resolving') return state;

    state = stepStretch(state, { type: 'step' }); // → events
    state = stepStretch(state, { type: 'step' }); // → planning or ended

    if (state.lastResolution) {
      const r = state.lastResolution;
      for (const ar of r.activityResults) {
        for (const note of ar.notes) appendLog(`  ${note}`);
        if (ar.tilesRevealed > 0) appendLog(`  Revealed ${ar.tilesRevealed} tiles`);
      }
      if (r.wanderingMonster.checked) {
        if (r.wanderingMonster.triggered) {
          appendLog(`  !! Wandering monster: ${r.wanderingMonster.encounter?.description ?? 'Unknown'}`);
        } else {
          appendLog(`  Monster check: safe (rolled ${r.wanderingMonster.rolled})`);
        }
      }
      for (const te of r.timelineEventsFired) {
        appendLog(`  Event: ${(te.data as { description?: string }).description ?? te.type}`);
      }
      for (const cd of r.countdownsExpired) {
        appendLog(`  Countdown expired: ${cd.label}`);
      }
      for (const ls of r.lightSourcesDepleted) {
        appendLog(`  Light depleted: ${ls.type} (${ls.carrierId})`);
      }
    }

    if (state.phase === 'ended') {
      appendLog(`Exploration ended: ${state.outcome}`);
    } else {
      appendLog(`--- Stretch ${state.stretchNumber} — Planning ---`);
    }

    return state;
  }, [appendLog]);

  /** Finalize state after resolution: sync map entities and update React state. */
  const finalizeState = useCallback((state: StretchState) => {
    const updatedMap = syncPlayerEntities(state);
    setStretchState({ ...state, map: updatedMap });
    setPendingActivities({});
  }, []);

  const commitPlayer = useCallback((playerId: string, activity: StretchActivity) => {
    if (!stretchState) return;

    let state = stepStretch(stretchState, { type: 'commit', playerId, activity });
    appendLog(`${stretchState.players.find(p => p.id === playerId)?.name ?? playerId}: ${activity.type}`);

    state = resolveAndLog(state);
    finalizeState(state);
  }, [stretchState, appendLog, resolveAndLog, finalizeState]);

  const commitAllWait = useCallback(() => {
    if (!stretchState) return;

    const uncommitted = stretchState.players.filter(p => !p.hasCommitted);
    let state = stretchState;
    for (const player of uncommitted) {
      const activity = pendingActivities[player.id] ?? { type: 'wait' as const };
      state = stepStretch(state, { type: 'commit', playerId: player.id, activity });
      appendLog(`${player.name}: ${activity.type}`);
    }

    state = resolveAndLog(state);
    finalizeState(state);
  }, [stretchState, pendingActivities, appendLog, resolveAndLog, finalizeState]);

  // ============================================================================
  // STEALTH TOGGLE
  // ============================================================================

  const handleToggleStealth = useCallback(() => {
    if (!stretchState) return;
    setStretchState(toggleStealthMode(stretchState));
  }, [stretchState]);

  // ============================================================================
  // KEYBOARD: WASD MOVEMENT
  // ============================================================================

  useEffect(() => {
    if (!stretchState || mode !== 'playing') return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      if (!stretchState || stretchState.phase !== 'planning') return;

      const pending = pendingActivities[activePlayerId];
      if (!pending || !MOVEMENT_ACTIVITIES.has(pending.type)) return;

      const dirMap: Record<string, { dx: number; dy: number }> = {
        w: { dx: 0, dy: -1 }, W: { dx: 0, dy: -1 }, ArrowUp: { dx: 0, dy: -1 },
        s: { dx: 0, dy: 1 }, S: { dx: 0, dy: 1 }, ArrowDown: { dx: 0, dy: 1 },
        a: { dx: -1, dy: 0 }, A: { dx: -1, dy: 0 }, ArrowLeft: { dx: -1, dy: 0 },
        d: { dx: 1, dy: 0 }, D: { dx: 1, dy: 0 }, ArrowRight: { dx: 1, dy: 0 },
      };

      const dir = dirMap[e.key];
      if (!dir) return;
      e.preventDefault();

      const player = stretchState.players.find(p => p.id === activePlayerId);
      if (!player) return;

      // Get current end of path (or player position)
      const currentPath = 'path' in pending && Array.isArray(pending.path) ? pending.path : [];
      const last = currentPath.length > 0
        ? currentPath[currentPath.length - 1]
        : { x: player.x, y: player.y };

      const nx = last.x + dir.dx;
      const ny = last.y + dir.dy;

      // Validate: in bounds, floor/door tile, not already in path
      if (nx < 0 || nx >= stretchState.map.width || ny < 0 || ny >= stretchState.map.height) return;
      const cell = getCell(stretchState.map, nx, ny);
      if (cell.terrain === 'wall' || cell.terrain === 'void' || cell.terrain === 'water') return;

      // Check movement budget (explore & move_quietly use half speed)
      const budget = player.movementBudget;
      const isHalfSpeed = pending.type === 'move_quietly' || pending.type === 'explore';
      const maxPath = isHalfSpeed ? Math.floor(budget * 0.5) : budget;
      if (currentPath.length >= maxPath) return;

      const newPath = [...currentPath, { x: nx, y: ny }];
      setPendingActivities(prev => ({
        ...prev,
        [activePlayerId]: { ...pending, path: newPath } as StretchActivity,
      }));
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [stretchState, mode, activePlayerId, pendingActivities]);

  // Toggle fog with F key
  useEffect(() => {
    if (mode !== 'playing') return;
    function handleKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'f' || e.key === 'F') {
        setRenderConfig(prev => ({ ...prev, fogEnabled: !prev.fogEnabled }));
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mode]);

  // ============================================================================
  // CELL CLICK
  // ============================================================================

  const handleCellClick = useCallback((x: number, y: number) => {
    if (!stretchState || stretchState.phase !== 'planning') return;
    const pending = pendingActivities[activePlayerId];
    if (!pending) return;

    if (pending.type === 'interact') {
      setPendingActivities(prev => ({
        ...prev,
        [activePlayerId]: { type: 'interact', x, y },
      }));
    }
  }, [stretchState, activePlayerId, pendingActivities]);

  // ============================================================================
  // BUILD GRID MAP STATE
  // ============================================================================

  const gridMapState = stretchState ? buildGridMapState(stretchState, activePlayerId) : null;

  // Path preview overlay for movement activities
  const pathPreview = (() => {
    if (!stretchState || !activePlayerId) return undefined;
    const pending = pendingActivities[activePlayerId];
    if (!pending || !MOVEMENT_ACTIVITIES.has(pending.type)) return undefined;
    const path = 'path' in pending && Array.isArray(pending.path) ? pending.path : [];
    if (path.length === 0) return undefined;
    return path.map((p: { x: number; y: number }) => ({ x: p.x, y: p.y }));
  })();

  // Interact target indicator
  const targetIndicators = (() => {
    if (!stretchState || !activePlayerId) return undefined;
    const pending = pendingActivities[activePlayerId];
    if (!pending || pending.type !== 'interact') return undefined;
    if ('x' in pending && pending.x > 0) {
      return [{ x: pending.x, y: pending.y, type: 'move' as const }];
    }
    return undefined;
  })();

  // ============================================================================
  // RESET
  // ============================================================================

  const handleReset = useCallback(() => {
    setMode('setup');
    setStretchState(null);
    setPendingActivities({});
    setLogEntries([]);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (mode === 'setup') {
    return <SetupScreen
      genConfig={genConfig}
      setGenConfig={setGenConfig}
      playerConfigs={playerConfigs}
      addPlayer={addPlayer}
      removePlayer={removePlayer}
      updatePlayer={updatePlayer}
      monsterConfig={monsterConfig}
      setMonsterConfig={setMonsterConfig}
      extras={extras}
      setExtras={setExtras}
      randomizeSeed={randomizeSeed}
      onStart={startExploration}
    />;
  }

  if (!stretchState) return null;

  const activePlayer = stretchState.players.find(p => p.id === activePlayerId);
  const pendingForActive = pendingActivities[activePlayerId] ?? null;
  const hasCommitted = activePlayer?.hasCommitted ?? false;
  const isPlanning = stretchState.phase === 'planning';
  const isEnded = stretchState.phase === 'ended';
  const otherPlayers = stretchState.players
    .filter(p => p.id !== activePlayerId)
    .map(p => ({ id: p.id, name: p.name }));

  return (
    <div className="flex flex-col h-screen bg-black text-white">
      {/* Status bar */}
      <StretchStatusBar state={stretchState} />

      {/* Countdowns */}
      {stretchState.countdowns.some(c => c.visible) && (
        <CountdownDisplay
          countdowns={stretchState.countdowns}
          className="px-4 py-2 border-b border-white/5"
        />
      )}

      {/* Main layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel — Players + Strategy */}
        <div className="w-56 border-r border-white/10 flex flex-col overflow-y-auto bg-neutral-950/50">
          <div className="p-3 space-y-3">
            {/* Player tabs */}
            <div className="space-y-1">
              <span className="text-xs text-neutral-500 font-medium">Players</span>
              {stretchState.players.map(player => (
                <button
                  key={player.id}
                  onClick={() => setActivePlayerId(player.id)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors text-left',
                    player.id === activePlayerId
                      ? 'bg-white/10 border border-white/20 text-white'
                      : 'border border-transparent text-neutral-400 hover:text-white hover:bg-white/5',
                  )}
                >
                  <span className={cn(
                    'w-2 h-2 rounded-full',
                    player.hasCommitted ? 'bg-green-500' : 'bg-neutral-600',
                  )} />
                  <span className="flex-1 truncate">{player.name}</span>
                  {player.id === activePlayerId && (
                    <ChevronRight className="w-3 h-3 text-neutral-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Crawl strategy */}
            <CrawlStrategyPanel
              strategy={stretchState.crawlStrategy}
              onToggleStealth={handleToggleStealth}
            />

            {/* Position info */}
            {activePlayer && (
              <div className="text-xs text-neutral-500 space-y-0.5">
                <div>Position: ({activePlayer.x}, {activePlayer.y})</div>
                <div>Move budget: {activePlayer.movementBudget}</div>
                {pendingForActive && 'path' in pendingForActive && Array.isArray((pendingForActive as { path?: unknown[] }).path) && (
                  <div>Path: {((pendingForActive as { path: unknown[] }).path).length} / {activePlayer.movementBudget} tiles</div>
                )}
              </div>
            )}

            {/* Quick actions */}
            <div className="space-y-1.5 pt-2 border-t border-white/5">
              {isPlanning && (
                <button
                  onClick={commitAllWait}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded bg-neutral-800/60 border border-white/10 text-neutral-300 hover:text-white hover:bg-neutral-700/60 transition-colors"
                >
                  <Play className="w-3 h-3" />
                  Commit All & Resolve
                </button>
              )}
              <button
                onClick={handleReset}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded bg-neutral-800/60 border border-white/10 text-neutral-400 hover:text-white hover:bg-neutral-700/60 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Center — Map */}
        <div className="flex-1 relative">
          {gridMapState && (
            <GridMapCanvas
              state={gridMapState}
              config={renderConfig}
              onCellClick={handleCellClick}
              pathPreview={pathPreview}
              targetIndicators={targetIndicators}
              activeEntityId={activePlayerId}
            />
          )}
        </div>

        {/* Right panel — Activity + Resolution */}
        <div className="w-72 border-l border-white/10 flex flex-col overflow-y-auto bg-neutral-950/50">
          <div className="p-3 space-y-3 flex-1">
            {/* Activity selection (planning, not committed) */}
            {isPlanning && !hasCommitted && (
              <>
                <ActivityPanel
                  selectedActivity={pendingForActive}
                  onSelectActivity={(activity) => {
                    setPendingActivities(prev => ({
                      ...prev,
                      [activePlayerId]: activity,
                    }));
                  }}
                  onConfirm={() => {
                    if (pendingForActive) {
                      commitPlayer(activePlayerId, pendingForActive);
                    }
                  }}
                  disabled={false}
                  playerNames={otherPlayers}
                />
                {MOVEMENT_ACTIVITIES.has(pendingForActive?.type ?? '') && (
                  <div className="text-xs text-neutral-600 bg-neutral-900/40 rounded px-2 py-1.5 border border-white/5">
                    Use WASD to build path. Click Lock In to commit.
                  </div>
                )}
                {pendingForActive?.type === 'interact' && (
                  <div className="text-xs text-neutral-600 bg-neutral-900/40 rounded px-2 py-1.5 border border-white/5">
                    Click a map cell to set interact target.
                  </div>
                )}
              </>
            )}

            {/* Waiting state */}
            {isPlanning && hasCommitted && (
              <div className="text-center py-4">
                <div className="text-xs text-neutral-500">Activity locked in</div>
                <div className="text-xs text-neutral-600 mt-1">
                  Switch to another player or "Commit All"
                </div>
              </div>
            )}

            {/* Resolution results */}
            {stretchState.lastResolution && !isPlanning && (
              <StretchResolutionPanel
                resolution={stretchState.lastResolution}
                onNextStretch={() => {
                  // Already auto-advanced — just log
                  appendLog(`Ready for stretch ${stretchState.stretchNumber}`);
                }}
                isEnded={isEnded}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar — Log */}
      <div className="border-t border-white/10 bg-neutral-950/80">
        <div
          ref={logRef}
          className="h-28 overflow-y-auto px-4 py-2 space-y-0.5 text-xs font-mono text-neutral-400"
        >
          {logEntries.map((entry, i) => (
            <div key={i} className={cn(getLogEntryColor(entry))}>
              {entry}
            </div>
          ))}
          {logEntries.length === 0 && (
            <div className="text-neutral-600 italic">Log will appear here...</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SETUP SCREEN
// ============================================================================

interface SetupScreenProps {
  genConfig: GeneratorConfig;
  setGenConfig: React.Dispatch<React.SetStateAction<GeneratorConfig>>;
  playerConfigs: PlayerSetup[];
  addPlayer: () => void;
  removePlayer: () => void;
  updatePlayer: (idx: number, updates: Partial<PlayerSetup>) => void;
  monsterConfig: MonsterSetup;
  setMonsterConfig: React.Dispatch<React.SetStateAction<MonsterSetup>>;
  extras: ExtrasSetup;
  setExtras: React.Dispatch<React.SetStateAction<ExtrasSetup>>;
  randomizeSeed: () => void;
  onStart: () => void;
}

function SetupScreen({
  genConfig, setGenConfig,
  playerConfigs, addPlayer, removePlayer, updatePlayer,
  monsterConfig, setMonsterConfig,
  extras, setExtras,
  randomizeSeed, onStart,
}: SetupScreenProps) {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <Link to="/games" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-white transition-colors mb-2">
            <ArrowLeft className="w-3 h-3" />
            Home
          </Link>
          <h1 className="text-xl font-bold">Stretch Simulator</h1>
          <p className="text-sm text-neutral-400 mt-0.5">Test the turn-based dungeon exploration engine</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Map Generator */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-neutral-300 flex items-center gap-2">
            <Footprints className="w-4 h-4 text-blue-400" />
            Map Generator
          </h2>
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-neutral-500">Type</label>
              <select
                value={genConfig.type}
                onChange={e => setGenConfig(prev => ({ ...prev, type: e.target.value as GeneratorType }))}
                className="w-full px-2 py-1.5 text-xs bg-neutral-900 border border-white/10 rounded text-white"
              >
                {Object.entries(GENERATOR_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-neutral-500">Width</label>
              <input
                type="number"
                value={genConfig.width}
                onChange={e => setGenConfig(prev => ({ ...prev, width: +e.target.value }))}
                className="w-full px-2 py-1.5 text-xs bg-neutral-900 border border-white/10 rounded text-white"
                min={10} max={80}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-neutral-500">Height</label>
              <input
                type="number"
                value={genConfig.height}
                onChange={e => setGenConfig(prev => ({ ...prev, height: +e.target.value }))}
                className="w-full px-2 py-1.5 text-xs bg-neutral-900 border border-white/10 rounded text-white"
                min={10} max={60}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-neutral-500">Seed</label>
              <div className="flex gap-1">
                <input
                  type="number"
                  value={genConfig.seed}
                  onChange={e => setGenConfig(prev => ({ ...prev, seed: +e.target.value }))}
                  className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-neutral-900 border border-white/10 rounded text-white"
                />
                <button
                  onClick={randomizeSeed}
                  className="px-2 py-1.5 text-xs bg-neutral-800 border border-white/10 rounded hover:bg-neutral-700 transition-colors"
                  title="Randomize seed"
                >
                  <RotateCcw className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Players */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-300 flex items-center gap-2">
              <Users className="w-4 h-4 text-green-400" />
              Players ({playerConfigs.length})
            </h2>
            <div className="flex gap-1">
              <button
                onClick={removePlayer}
                disabled={playerConfigs.length <= 1}
                className="p-1 rounded hover:bg-white/10 text-neutral-500 hover:text-white disabled:opacity-30 transition-colors"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={addPlayer}
                disabled={playerConfigs.length >= 4}
                className="p-1 rounded hover:bg-white/10 text-neutral-500 hover:text-white disabled:opacity-30 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {playerConfigs.map((pc, i) => (
              <div key={pc.id} className="flex items-center gap-3 bg-neutral-900/60 rounded-lg px-3 py-2 border border-white/5">
                <span className="text-xs text-neutral-500 w-6">P{i + 1}</span>
                <input
                  type="text"
                  value={pc.name}
                  onChange={e => updatePlayer(i, { name: e.target.value })}
                  className="flex-1 px-2 py-1 text-xs bg-neutral-800/50 border border-white/10 rounded text-white"
                />
                <div className="flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5 text-yellow-400" />
                  <select
                    value={pc.lightType}
                    onChange={e => updatePlayer(i, { lightType: e.target.value as LightSourceType })}
                    className="px-2 py-1 text-xs bg-neutral-800/50 border border-white/10 rounded text-white"
                  >
                    {Object.entries(LIGHT_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Wandering Monsters */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-neutral-300">Wandering Monsters</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-neutral-500">Frequency</label>
              <select
                value={monsterConfig.frequency}
                onChange={e => setMonsterConfig(prev => ({ ...prev, frequency: e.target.value as WanderingFrequency }))}
                className="w-full px-2 py-1.5 text-xs bg-neutral-900 border border-white/10 rounded text-white"
              >
                {Object.entries(FREQUENCY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-neutral-500">Likelihood</label>
              <select
                value={monsterConfig.likelihood}
                onChange={e => setMonsterConfig(prev => ({ ...prev, likelihood: e.target.value as WanderingLikelihood }))}
                className="w-full px-2 py-1.5 text-xs bg-neutral-900 border border-white/10 rounded text-white"
              >
                {Object.entries(LIKELIHOOD_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
            <input
              type="checkbox"
              checked={monsterConfig.addSampleTable}
              onChange={e => setMonsterConfig(prev => ({ ...prev, addSampleTable: e.target.checked }))}
              className="rounded border-white/20 bg-neutral-800"
            />
            Add sample encounter table (rats / skeletons / wraith)
          </label>
        </section>

        {/* Extras */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-neutral-300">Extras</h2>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={extras.addCountdown}
                onChange={e => setExtras(prev => ({ ...prev, addCountdown: e.target.checked }))}
                className="rounded border-white/20 bg-neutral-800"
              />
              Add sample countdown (5 stretches — "Ritual completes")
            </label>
            <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={extras.addTimelineEvent}
                onChange={e => setExtras(prev => ({ ...prev, addTimelineEvent: e.target.checked }))}
                className="rounded border-white/20 bg-neutral-800"
              />
              Add sample timeline event (stretch 3 — dungeon rumble)
            </label>
          </div>
        </section>

        {/* Start button */}
        <button
          onClick={onStart}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium text-sm transition-colors"
        >
          <Play className="w-4 h-4" />
          Start Exploration
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/** Return a Tailwind color class for a log entry based on its prefix. */
function getLogEntryColor(entry: string): string {
  if (entry.startsWith('  !!') || entry.startsWith('Error')) return 'text-red-400';
  if (entry.startsWith('  Event')) return 'text-purple-400';
  if (entry.startsWith('  Light depleted')) return 'text-amber-400';
  if (entry.startsWith('---')) return 'text-blue-400 font-medium';
  return 'text-neutral-400';
}

/** Build a GridMapState from stretch state for the GridMapCanvas component. */
function buildGridMapState(stretch: StretchState, playerId: string): GridMapState | null {
  const player = stretch.players.find(p => p.id === playerId);
  if (!player) return null;

  return {
    map: stretch.map,
    playerId,
    fogGrid: player.fogGrid,
    lightGrid: player.lightGrid,
    openDoors: stretch.openDoors instanceof Set ? stretch.openDoors : new Set(stretch.openDoors as unknown as string[]),
    lockedDoors: new Set<string>(),
    firedTriggers: stretch.firedTriggers instanceof Set ? stretch.firedTriggers : new Set(stretch.firedTriggers as unknown as string[]),
    triggerFlags: stretch.triggerFlags instanceof Set ? stretch.triggerFlags : new Set(stretch.triggerFlags as unknown as string[]),
    pendingEvents: [],
    moveCount: 0,
  };
}

/** Sync player entity positions on the map after stretch resolution. */
function syncPlayerEntities(state: StretchState): GridMap {
  const updatedEntities = state.map.entities.map(entity => {
    const player = state.players.find(p => p.id === entity.id);
    if (player) {
      return { ...entity, x: player.x, y: player.y };
    }
    return entity;
  });
  return { ...state.map, entities: updatedEntities };
}
